import { Editor, Node } from 'slate';
import {
  Prism,
  normalizeTokens,
  type NormalizedToken,
} from '../../util/prismLanguages';
import type {
  DecorateNodeCallback,
  EditorPlugin,
} from '../../types/aha-editor';
import { isCodeBlockElement } from '../../types/guards';
import DecorationUtils from '../../AhaEditorUtils/DecorationUtils';

// Generate decorations for all code blocks in the editor for syntax highlighting.
const decorateForSyntaxHighlighting = (
  editor: Editor,
  decorateNode: DecorateNodeCallback
): void => {
  // Find all code blocks that might need to be decorated.
  const blockEntries = Array.from(
    Editor.nodes(editor, {
      at: [],
      mode: 'highest',
      match: isCodeBlockElement,
    })
  );

  for (const [node, nodePath] of blockEntries) {
    const language = node.code_language;
    if (!language || language === '') continue;

    const text = Node.string(node);
    const grammar = Prism.languages[language];
    if (!grammar) continue;

    const rawTokens = Prism.tokenize(text, grammar);
    if (rawTokens.length === 0) continue;
    const tokens: NormalizedToken[] = normalizeTokens(rawTokens);
    let tokenIndex = 0;
    let tokenRemaining = tokens[0].length;

    // Walk through all child text nodes and map the tokens to them
    // as we go.
    for (const [textNode, textPath] of Node.texts(node)) {
      let textNodeRemaining = textNode.text.length;
      let token = tokens[tokenIndex];
      const path = [...nodePath, ...textPath];

      let start = 0;
      while (token && textNodeRemaining > 0) {
        if (textNodeRemaining <= tokenRemaining) {
          // Use the token for the whole node.
          const end = start + textNodeRemaining;
          if (Array.isArray(token.type)) {
            decorateNode(
              textNode,
              { anchor: { path, offset: start }, focus: { path, offset: end } },
              {
                classNames: Object.fromEntries(
                  token.type.map((type: string) => [
                    DecorationUtils.decorationClass(`prism-${type}`),
                    true,
                  ])
                ),
              }
            );
          }
          start += textNodeRemaining;
          tokenRemaining -= textNodeRemaining;
          textNodeRemaining = 0;
        } else {
          // The token is less than the text node size, so use the remaining token.
          const end = start + tokenRemaining;
          // Use the token for part of the node.
          if (Array.isArray(token.type)) {
            decorateNode(
              textNode,
              { anchor: { path, offset: start }, focus: { path, offset: end } },
              {
                classNames: Object.fromEntries(
                  token.type.map((type: string) => [
                    DecorationUtils.decorationClass(`prism-${type}`),
                    true,
                  ])
                ),
              }
            );
          }
          start += tokenRemaining;
          textNodeRemaining -= tokenRemaining;
          tokenIndex += 1;
          token = tokens[tokenIndex];
          if (tokenIndex >= tokens.length) break;
          tokenRemaining = tokens[tokenIndex].length;
        }
      }
    }
  }
};

const CodeBlockDecoratorPlugin: EditorPlugin = {
  name: 'Code block syntax highlighting decorator',
  decorator: {
    decorate: decorateForSyntaxHighlighting,
  },
};

export default CodeBlockDecoratorPlugin;
